import React, { useState } from 'react';
import { X, Mail, Phone, Eye, EyeOff } from 'lucide-react';
import { supabase } from './supabaseClient';

const SignupModal = ({ 
  showSignupModal, 
  setShowSignupModal, 
  signupData, 
  setSignupData, 
  countries,
  setShowLandingPage 
}) => {
  const [signupStep, setSignupStep] = useState('details'); // 'details' or 'otp'
  const [signupMethod, setSignupMethod] = useState('email');
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingUserId, setPendingUserId] = useState(null);

  if (!showSignupModal) return null;

  const handleSignup = async () => {
    setError('');
    
    if (!signupData.firstName || !signupData.lastName) {
      setError('Please enter your first and last name');
      return;
    }

    if (!signupData.email) {
      setError('Please enter your email address');
      return;
    }

    if (!signupData.password || signupData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (signupData.password !== signupData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      // Create user with password
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: signupData.email.toLowerCase(),
        password: signupData.password,
        options: {
          emailRedirectTo: window.location.origin,
          data: {
            first_name: signupData.firstName,
            last_name: signupData.lastName,
            phone: signupData.phone || null,
            country: signupData.country
          }
        }
      });

      if (authError) throw authError;

      setPendingUserId(authData.user.id);

      // Create user profile in database
      const { error: profileError } = await supabase
        .from('users')
        .insert([{
          id: authData.user.id,
          email: signupData.email.toLowerCase(),
          phone: signupData.phone || null,
          first_name: signupData.firstName,
          last_name: signupData.lastName,
          country: signupData.country,
          kyc_level: 'minimal',
          remaining_limit: 900
        }]);

      if (profileError && profileError.code !== '23505') {
        console.error('Profile error:', profileError);
      }

      // Move to OTP verification step
      setSignupStep('otp');
      alert('Verification code sent to your email!');
      
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email: signupData.email.toLowerCase(),
        token: otp,
        type: 'signup'
      });

      if (error) throw error;

      // Clear old data
      localStorage.removeItem('globalpay_balances');
      localStorage.removeItem('globalpay_cards');
      localStorage.removeItem('globalpay_wallets');

      alert('Account verified successfully!');
      setShowSignupModal(false);
      setShowLandingPage(false);
      window.location.reload();
      
    } catch (error) {
      setError('Invalid verification code. Please try again.');
      console.error('OTP verification error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-purple-800 rounded-lg p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold">
            {signupStep === 'details' ? 'Create Account' : 'Verify Your Email'}
          </h3>
          <button onClick={() => setShowSignupModal(false)}>
            <X size={24} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-900 rounded text-red-200 text-sm">
            {error}
          </div>
        )}

        {signupStep === 'details' ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="First Name"
                value={signupData.firstName}
                onChange={(e) => setSignupData({...signupData, firstName: e.target.value})}
                className="w-full bg-purple-700 text-white p-3 rounded"
              />
              <input
                type="text"
                placeholder="Last Name"
                value={signupData.lastName}
                onChange={(e) => setSignupData({...signupData, lastName: e.target.value})}
                className="w-full bg-purple-700 text-white p-3 rounded"
              />
            </div>

            <input
              type="email"
              placeholder="Email"
              value={signupData.email}
              onChange={(e) => setSignupData({...signupData, email: e.target.value})}
              className="w-full bg-purple-700 text-white p-3 rounded"
            />

            <input
              type="tel"
              placeholder="Phone (optional)"
              value={signupData.phone}
              onChange={(e) => setSignupData({...signupData, phone: e.target.value})}
              className="w-full bg-purple-700 text-white p-3 rounded"
            />

            <input
              type="password"
              placeholder="Password (8+ characters)"
              value={signupData.password}
              onChange={(e) => setSignupData({...signupData, password: e.target.value})}
              className="w-full bg-purple-700 text-white p-3 rounded"
            />

            <input
              type="password"
              placeholder="Confirm Password"
              value={signupData.confirmPassword}
              onChange={(e) => setSignupData({...signupData, confirmPassword: e.target.value})}
              className="w-full bg-purple-700 text-white p-3 rounded"
            />

            <select
              value={signupData.country}
              onChange={(e) => setSignupData({...signupData, country: e.target.value})}
              className="w-full bg-purple-700 text-white p-3 rounded"
            >
              {countries.map(c => (
                <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
              ))}
            </select>

            <button
              onClick={handleSignup}
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Account'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-blue-900 bg-opacity-30 border border-blue-600 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-200">
                <strong>📧 Email Verification</strong>
              </p>
              <p className="text-sm text-blue-300 mt-1">
                We've sent a 6-digit code to verify your email address.
              </p>
            </div>

            <p className="text-center text-purple-200">
              Enter the code sent to<br />
              <span className="font-bold">{signupData.email}</span>
            </p>

            <div>
              <label className="block text-sm font-medium mb-2 text-center">Verification Code</label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none text-center text-2xl tracking-widest"
                placeholder="000000"
                maxLength={6}
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && otp.length === 6 && handleVerifyOTP()}
              />
            </div>

            <button
              onClick={handleVerifyOTP}
              disabled={loading || otp.length !== 6}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded disabled:opacity-50"
            >
              {loading ? 'Verifying...' : 'Verify & Continue'}
            </button>

            <button
              onClick={() => setSignupStep('details')}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white py-2 rounded"
            >
              Back
            </button>

            <p className="text-sm text-center text-purple-300">
              Didn't receive the code?{' '}
              <button 
                onClick={async () => {
                  try {
                    await supabase.auth.resend({
                      type: 'signup',
                      email: signupData.email.toLowerCase()
                    });
                    alert('Verification code resent!');
                  } catch (error) {
                    setError('Failed to resend code. Please try again.');
                  }
                }}
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Resend
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignupModal;